/**
 * The bark box (docs/ux/project-tab-spec.md §1.2): one box per project tab. Text, dictation,
 * attach, paste and drop (the shared `useAttachmentInput` / `AttachmentDropZone`; a screenshot is a
 * paste or an attachment), Quick or Discuss (toggle, `?? ` / `/discuss` prefix, ⇧⌘↵). Submit writes
 * the sealed item and shows it at once. The row follows the refinement spec (WP-5): target chip
 * (`⌥1 Viko ▾`, route behind its menu), input, attach, mic, Quick|Discuss, `/ to type`, Send `⌘↵`.
 */
import type { BarkRouting } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { Mic, MicOff, Paperclip, Send } from 'lucide-react'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../../lib/attachment-input.js'
import { useBackend } from '../../lib/backend.js'
import { cleanRouting, isAutoRouting, useBarkRoute } from '../../lib/bark-route.js'
import { parseBarkInput } from '../../lib/discuss-store.js'
import { useRun } from '../../lib/queries.js'
import { isStandalone, tabShortcut } from '../../lib/shortcut.js'
import { useDictation } from '../../lib/speech.js'
import { useTabNumber } from '../../lib/tab-number.js'
import { AttachmentDropZone } from '../AttachmentDropZone.js'
import { storage } from '../Capture.js'
import { Keys } from '../Keys.js'
import { errorText, useToast } from '../ui.js'
import { RouteChip } from './RouteChip.js'
import { useBarkStore, useTriageMachine } from './useBarks.js'

export interface BarkBoxProps {
  projectId: string
  projectName: string
  /** `large` on an empty project. */
  size?: 'normal' | 'large'
  /** Starter text picked from the empty state; `nonce` changes on every pick. */
  prefill?: { text: string; nonce: number }
  /** Locked or no content key: disabled with "Unlock to send". */
  disabled?: boolean
  /** The inspector shows this live run: offer "Send straight to this run instead". */
  inspectRunId?: string
  /** A Discuss item opens straight into its conversation. */
  onSubmitted?: (barkId: string, mode: 'quick' | 'discuss') => void
}

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform)
const SEND_KEYS = MAC ? '⌘ ↵' : 'Ctrl ↵'

export function BarkBox({
  projectId,
  projectName,
  size = 'normal',
  prefill,
  disabled: lockedByHost = false,
  inspectRunId,
  onSubmitted,
}: BarkBoxProps) {
  const b = useBackend()
  const store = useBarkStore()
  const toast = useToast()
  const triageMachine = useTriageMachine(projectId)
  // ---- bark-route: this bark's override, else the project or global default (Auto) ----
  const route = useBarkRoute(projectId)
  const [override, setOverride] = useState<BarkRouting | undefined>()
  const [routeOpen, setRouteOpen] = useState(false)
  const routing = cleanRouting(override ?? route.value)
  // The chosen machine triages and works the item itself, so the constraint is honored there.
  const machineId = routing.machineId ?? triageMachine
  // ---- end bark-route ----
  const liveRun = useRun(inspectRunId)
  const tabNumber = useTabNumber()
  const tabKey = tabNumber ? tabShortcut(tabNumber, isStandalone()) : undefined
  const [text, setText] = useState('')
  const [mode, setMode] = useState<'quick' | 'discuss'>('quick')
  const [dictated, setDictated] = useState(false)
  const [sending, setSending] = useState(false)
  const [announce, setAnnounce] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)
  const files = useRef<HTMLInputElement>(null)
  const id = useId()
  const disabled = lockedByHost || !store
  const input = useAttachmentInput({
    disabled,
    storageOff: storage.off,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setText((cur) => cur + t),
  })
  const dictation = useDictation((t) => {
    setDictated(true)
    setText((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t))
  })

  // bark-route: ⌥R opens the route chooser (the shortcut registry is not merged yet).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.metaKey && !e.ctrlKey && e.code === 'KeyR') {
        e.preventDefault()
        setRouteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!prefill) return
    setText(prefill.text)
    ref.current?.focus()
  }, [prefill])

  const reset = () => {
    setText('')
    setDictated(false)
    input.clear()
  }

  const submit = async (as?: 'quick' | 'discuss') => {
    if (disabled || sending) return
    const parsed = parseBarkInput(text, as ?? mode)
    if (!parsed.text && !input.files.length) return
    if (!input.canSend)
      return toast('Remove or fix the attachments marked with an error first.', 'error')
    if (!machineId)
      return toast('No machine can sort this yet. Add a machine to this project.', 'error')
    dictation.stop()
    setSending(true)
    input.markUploading(true)
    try {
      const bark = await store!.submit({
        projectId,
        machineId,
        text: parsed.text,
        mode: parsed.mode,
        dictated,
        files: input.files,
        ...(isAutoRouting(routing) ? {} : { routing }),
      })
      setOverride(undefined) // a one-off choice ends with its bark
      reset()
      setAnnounce('Sent — sorting it')
      onSubmitted?.(bark.id, parsed.mode)
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      input.retryable()
      input.markUploading(false)
      setSending(false)
    }
  }

  const sendDirect = async () => {
    if (!inspectRunId || !liveRun?.machineId || !store || !text.trim()) return
    const words = text.trim()
    try {
      await store.recordDirect({
        projectId,
        machineId: liveRun.machineId,
        runId: inspectRunId,
        text: words,
        send: (clientMessageId) =>
          b.dispatch('run.message', liveRun.machineId, {
            runId: inspectRunId,
            text: words,
            queue: true,
            clientMessageId,
          }),
      })
      reset()
      setAnnounce('Sent straight to the run')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  const placeholder = disabled
    ? 'Unlock to send'
    : size === 'large'
      ? `What should happen in ${projectName}? A goal, a bug, an idea. Say it however it comes.`
      : `Tell Viko anything about ${projectName}…`

  return (
    <form
      className={`lk-composer grid gap-2 rounded-xl border border-border-subtle bg-surface-1 p-3${
        text || input.files.length ? '' : ' is-empty'
      }`}
      aria-label={`Tell Viko about ${projectName}`}
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        void submit()
      }}
    >
      <AttachmentDropZone input={input}>
        <div className="lk-bark-row flex flex-wrap items-start gap-2">
          {disabled && !lockedByHost ? null : (
            <span className="lk-bark-target flex min-w-0 pt-0.5">
              <RouteChip
                projectId={projectId}
                projectName={projectName}
                value={routing}
                draft={text}
                tabKey={tabKey}
                disabled={disabled}
                open={routeOpen}
                onOpenChange={setRouteOpen}
                onChange={(r) => setOverride(r)}
                {...(route.ready && b.entities
                  ? {
                      onMakeDefault: async (r: BarkRouting) => {
                        await route.saveProject(r)
                        setOverride(undefined)
                      },
                    }
                  : {})}
              />
            </span>
          )}
          <label className="sr-only" htmlFor={`${id}-bark`}>
            Tell Viko about {projectName}
          </label>
          <textarea
            id={`${id}-bark`}
            ref={ref}
            data-primary-input
            rows={size === 'large' ? 4 : 2}
            value={dictation.interim ? `${text}${text ? ' ' : ''}${dictation.interim}` : text}
            disabled={disabled}
            placeholder={placeholder}
            className="min-w-[12rem] flex-1 resize-y rounded-lg border-0 bg-transparent p-1 text-[15px] leading-6 text-text-1 outline-none"
            onChange={(e) => setText(e.target.value)}
            onPaste={input.onPaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                void submit(e.shiftKey ? 'discuss' : undefined)
              }
              if (e.key === 'Escape') e.currentTarget.blur()
            }}
          />
        </div>
      </AttachmentDropZone>
      {inspectRunId && liveRun && text.trim() ? (
        <button
          type="button"
          className="justify-self-start rounded-full bg-accent-soft px-3 py-1 text-[14px] text-accent-text"
          onClick={() => void sendDirect()}
        >
          Send straight to this run instead
        </button>
      ) : null}
      <div className="lk-bark-tools flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          aria-label="Attach files or photos"
          icon={<Paperclip size={14} />}
          disabled={disabled}
          onClick={() => files.current?.click()}
        />
        {dictation.supported ? (
          <Button
            size="sm"
            variant="ghost"
            aria-pressed={dictation.listening}
            aria-label={dictation.listening ? 'Stop dictation' : 'Dictate'}
            icon={dictation.listening ? <MicOff size={14} /> : <Mic size={14} />}
            disabled={disabled}
            onClick={dictation.toggle}
          />
        ) : null}
        <div role="group" aria-label="Mode" className="flex rounded-lg bg-surface-2 p-0.5">
          {(['quick', 'discuss'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              title={m === 'quick' ? 'Viko acts now, with Undo' : 'Talk it through first (⇧⌘↵)'}
              className={`rounded-md px-2.5 py-1 text-[14px] ${
                mode === m ? 'bg-surface-1 text-text-1 shadow-sm' : 'text-text-2'
              }`}
              onClick={() => setMode(m)}
            >
              {m === 'quick' ? 'Quick' : 'Discuss'}
            </button>
          ))}
        </div>
        <span className="lk-bark-hint flex min-w-0 flex-1 justify-end text-[14px] text-text-3">
          {disabled && !lockedByHost ? (
            'Barks need an unlocked, end-to-end encrypted account.'
          ) : disabled ? null : (
            // Both hints stay laid out (one grid cell); focus only swaps which is visible, so
            // blurring the box never shifts the page under the pointer.
            <span className="lk-bark-hint__stack">
              <span className="lk-bark-hint__keys lk-bark-hint__idle">
                <Keys k="/" /> to type
              </span>
              <span className="lk-bark-hint__keys lk-bark-hint__typing">
                <Keys k={SEND_KEYS} /> send <Keys k={`⇧ ${SEND_KEYS}`} /> discuss
              </span>
            </span>
          )}
        </span>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          icon={<Send size={14} />}
          aria-keyshortcuts={MAC ? 'Meta+Enter' : 'Control+Enter'}
          disabled={disabled || sending || (!text.trim() && !input.files.length)}
        >
          {mode === 'discuss' ? 'Discuss' : 'Send'}
          <Keys k={SEND_KEYS} className="lk-send-keys" />
        </Button>
      </div>
      {dictation.error ? (
        <p role="alert" className="m-0 text-[14px] text-status-wait">
          {dictation.error}
        </p>
      ) : null}
      <input
        ref={files}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          input.add(e.target.files)
          e.target.value = ''
        }}
      />
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>
    </form>
  )
}
